import i18next from "i18next";
import React, { useEffect, useState } from "react";
import {
  EMPTY_RESULTS_SHOW,
  FIELD_REQUIRED,
  LOADING,
  PRODUCTS,
  SELECT_CATALOG_IN_CAMPAIGN_LEVEL,
} from "../../../../i18n/keysTranslations";
import { Controller, useController } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startGetCatalogsAdsCampaigns } from "../../../../actions/adsCampaigns";
import { useDebounced } from "../../../../hooks/useDebounce";
import { DELAY_DEBOUNCE, META } from "../../../../utils/constants";
import _ from "lodash";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";

const SelectProductsSetMeta = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  catalogID,
  onChangeAction = () => {},
  filteredValues = [],
  disableClearable = false,
  sxTextField = {},
}) => {
  const [open, setOpen] = useState(false);
  const [isCatalogSelected, setIsCatalogSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [catalog, setCatalog] = useState(null);

  const {
    field: { value: valueField },
  } = useController({ control, name });

  const onGetCatalogsAdsCampaigns = async () => {
    if (!catalogID && !valueField) {
      setIsCatalogSelected(false);
      return;
    }

    setIsCatalogSelected(true);
    setLoading(true);
    const response = await dispatch(
      startGetCatalogsAdsCampaigns({
        integrationID,
        adAccountID,
        catalogID,
        includedID: valueField,
        from: "productSet",
        platform: META,
      })
    );
    if (response.ok) {
      const data = response.data;
      if (data?.length > 0) {
        const catalog = data[0];
        const productSets = catalog.productSets || [];
        setOptions(productSets);
        setCatalog(catalog);
      }
    }
    setLoading(false);
  };

  const onSearchProductsSets = useDebounced(
    async (search) => {
      setLoading(true);
      const response = await dispatch(
        startGetCatalogsAdsCampaigns({
          integrationID,
          adAccountID,
          catalogID,
          name: search,
          from: "productSet",
          platform: META,
        })
      );
      if (response.ok) {
        const data = response.data;
        if (data?.length > 0) {
          const catalog = data[0];
          const productSets = catalog.productSets || [];
          setOptions((prev) => _.uniqBy([...prev, ...productSets], "id"));
        }
      }
      setLoading(false);
    },
    [DELAY_DEBOUNCE]
  );

  useEffect(() => {
    onGetCatalogsAdsCampaigns();
    // eslint-disable-next-line
  }, [integrationID, adAccountID, catalogID]);

  useEffect(() => {
    if (!valueField) return;
    const productSet = options.find((opt) => opt.id === valueField);
    onChangeAction(productSet || valueField, catalog);

    // eslint-disable-next-line
  }, [options]);

  const getOptionLabel = (option) => {
    if (option.count) {
      return `${option?.name} - ${option?.id} - (${option?.count} ${t(
        PRODUCTS
      )})`;
    }
    return `${option?.name} - ${option?.id}`;
  };

  return (
    <>
      <Controller
        defaultValue={""}
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Autocomplete
            {...field}
            id={name}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            disableClearable={disableClearable}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            onInputChange={(event, newInputValue) => {
              if (!newInputValue || !event) return;
              onSearchProductsSets(newInputValue);
            }}
            noOptionsText={
              isCatalogSelected
                ? t(EMPTY_RESULTS_SHOW)
                : t(SELECT_CATALOG_IN_CAMPAIGN_LEVEL)
            }
            loading={loading}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={(_, value) => {
              if (value?.id) {
                field.onChange(value.id);
                onChangeAction(value, catalog);
              } else {
                field.onChange(null);
                onChangeAction(null, catalog);
              }
            }}
            isOptionEqualToValue={(option, value) => option?.id === value}
            getOptionLabel={(option) => {
              const optionValue = options.find((opt) => opt.id === option);
              if (optionValue) {
                return getOptionLabel(optionValue);
              }
              if (typeof option === "string") {
                return option;
              }
              return getOptionLabel(option);
            }}
            options={options.filter((opt) => !filteredValues.includes(opt.id))}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={option.id}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="body2">
                    {getOptionLabel(option)}
                  </Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                hiddenLabel={!label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                error={!!error}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                  ".MuiFormHelperText-root": {
                    ml: "0 !important",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
      />
      {(error?.message || helperText) && (
        <Typography
          variant="caption"
          color={error?.message ? "error" : "fields.placeholder"}
        >
          {error?.message || helperText}
        </Typography>
      )}
    </>
  );
};

export default SelectProductsSetMeta;
